/**
 * Asset intent — what the DESIGNER said about an image, in one place.
 *
 * Design JSON V2 expresses image INTENT, not image generation. The model
 * decides whether a design needs a photograph, what it should show, how it
 * should be cropped, and what it must feel like. This module reads those
 * decisions off a document and prepares them for an image provider. It makes no
 * creative decision of its own, and that boundary is the point: every term that
 * reaches a search here was written by the model.
 */

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const isStr = (v) => typeof v === 'string' && v.trim() !== '';

/** Node types that can carry a photograph. */
export const IMAGE_NODE_TYPES = Object.freeze(['image', 'frame']);

/** Orientations a provider understands. Anything else is the caller's typo. */
export const ORIENTATIONS = Object.freeze(['landscape', 'portrait', 'square']);

/**
 * A src that is not a real, fetchable image.
 *
 * Both shapes have actually reached the browser: `{{IMAGE1}}` from the schema's
 * own placeholder convention, and `path/to/dental-clinic.jpg` and
 * `https://example.com/hero.jpg` invented by the model when it was asked for a
 * URL it could not know. None of them may survive into a preview.
 */
export const isUnusableSrc = (src) => {
  if (!isStr(src)) return true;
  const s = src.trim();
  if (s.startsWith('{{') || s.includes('{{')) return true;
  if (/^(path\/|\.?\/)/i.test(s)) return true;
  if (/^https?:\/\/(www\.)?example\.(com|org|net)/i.test(s)) return true;
  return !/^https?:\/\//i.test(s) && !s.startsWith('data:');
};

/** True when this node still needs a real photograph. */
export const needsResolution = (el) =>
  IMAGE_NODE_TYPES.includes(el && el.type) && isUnusableSrc(el && el.src);

/**
 * Normalize whatever the model wrote into the shape the resolver sends.
 *
 * `query` is the older single-string field and still appears in documents the
 * model produces; it is read as the search query so an older document resolves
 * rather than silently going unillustrated.
 */
export const normalizeIntent = (raw, fallback = {}) => {
  const intent = asObject(raw);
  const subject = isStr(intent.subject) ? intent.subject.trim() : (isStr(fallback.query) ? fallback.query.trim() : '');
  const searchQuery = isStr(intent.searchQuery) ? intent.searchQuery.trim() : subject;
  const orientation = ORIENTATIONS.includes(intent.orientation) ? intent.orientation : null;
  // The role may come from the intent the model wrote OR from the section the
  // node sits in. Reading only the former marked a hero frame as optional,
  // because the renderer records its role as `sectionRole`.
  const role = isStr(intent.role) ? intent.role.trim() : (isStr(fallback.role) ? fallback.role.trim() : '');

  return {
    id: fallback.id || intent.id || '',
    role,
    subject,
    searchQuery,
    keywords: asArray(intent.keywords).filter(isStr).map((k) => k.trim()),
    mood: asArray(intent.mood).filter(isStr).map((m) => m.trim()),
    orientation,
    aspectPreference: isStr(intent.aspectPreference) ? intent.aspectPreference : null,
    visualStyle: isStr(intent.visualStyle) ? intent.visualStyle : null,
    cropStrategy: isStr(intent.cropStrategy) ? intent.cropStrategy : null,
    placementIntent: isStr(intent.placementIntent) ? intent.placementIntent : null,
    focalPoint: asObject(intent.focalPoint).x !== undefined ? intent.focalPoint : null,
    sourcePreference: isStr(intent.sourcePreference) ? intent.sourcePreference.toLowerCase() : null,
    // A hero is required whether or not the model remembered to say so: a page
    // whose hero silently goes unillustrated is the failure this whole flow exists to stop.
    required: intent.required === true || role.toLowerCase() === 'hero',
  };
};

/**
 * Every image intent in a RENDERED page set, in document order.
 *
 * Rendered elements are used rather than the design document because that is
 * where a V2 image node has become the element the preview will actually paint,
 * carrying the `designV2` metadata the model authored.
 *
 * @param {Array} pages  rendered pages
 * @returns {Array<{ id: string, intent: Object }>}
 */
export const collectAssetIntents = (pages) => {
  const out = [];
  asArray(pages).forEach((page) => {
    asArray(asObject(page).elements).forEach((el) => {
      if (!needsResolution(el)) return;
      const meta = asObject(el.designV2);
      const intent = normalizeIntent(meta.assetIntent, {
        id: el.id,
        role: meta.sectionRole || meta.role || '',
        // `imageQuery` is what the renderer already carries for a V2 image node.
        query: meta.imageQuery || el.query || '',
      });
      if (isStr(intent.searchQuery) || intent.keywords.length) out.push({ id: el.id, intent });
    });
  });
  return out;
};

/**
 * Progressively broader searches for one intent, best first.
 *
 * Every variant is built from terms the MODEL wrote — the full query, then the
 * subject alone, then its strongest keywords. Nothing new is invented: a
 * resolver that started adding its own subjects would be quietly art-directing
 * the page, which is the model's job.
 *
 * @returns {string[]} unique, non-empty queries
 */
export const refinementQueries = (intent) => {
  const i = asObject(intent);
  const keywords = asArray(i.keywords).filter(isStr);
  const variants = [
    i.searchQuery,
    i.subject,
    keywords.slice(0, 3).join(' '),
    keywords.slice(0, 2).join(' '),
    keywords[0],
  ];
  const seen = new Set();
  return variants
    .filter(isStr)
    .map((v) => v.trim())
    .filter((v) => {
      const key = v.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};
